/**
 * Reset password page — token in query string. Public route.
 */
import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";

export default function ResetPasswordPage() {
    const { api } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        if (pw !== pw2) { setError("Passwords do not match"); return; }
        if (pw.length < 8) { setError("Password must be at least 8 characters"); return; }
        setBusy(true); setError("");
        try {
            await api.post("/v2/auth/reset-password", { token, new_password: pw });
            setDone(true);
            setTimeout(() => navigate("/login"), 2500);
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
        } finally { setBusy(false); }
    };

    if (!token) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="reset-password-page">
                <div className="max-w-sm border border-red-500/40 bg-red-500/10 p-5 text-center">
                    <AlertCircle className="w-5 h-5 text-red-300 mx-auto mb-2" />
                    <p className="text-sm text-red-200">Missing reset token.</p>
                    <Link to="/forgot-password" className="text-xs text-gray-400 hover:text-white inline-block mt-3">Request a new reset link →</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="reset-password-page">
            <div className="w-full max-w-sm border border-border/40 bg-card p-7">
                <h1 className="text-xl font-display font-bold text-white mb-1">Set a new password</h1>
                <p className="text-xs text-gray-500 font-body mb-5">Choose a strong password (8+ characters).</p>

                {done ? (
                    <div className="text-center py-4" data-testid="reset-success">
                        <CheckCircle2 className="w-6 h-6 text-emerald-300 mx-auto mb-2" />
                        <p className="text-sm text-emerald-200">Password updated. Redirecting to login…</p>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-3">
                        <div>
                            <label className="text-[10px] font-mono uppercase text-gray-500">New password</label>
                            <input type="password" value={pw} onChange={e => setPw(e.target.value)} required minLength={8}
                                data-testid="reset-pw1"
                                className="w-full mt-1 bg-background border border-border/40 text-sm text-white px-2 py-2 focus:outline-none focus:border-white/30" />
                        </div>
                        <div>
                            <label className="text-[10px] font-mono uppercase text-gray-500">Confirm password</label>
                            <input type="password" value={pw2} onChange={e => setPw2(e.target.value)} required minLength={8}
                                data-testid="reset-pw2"
                                className="w-full mt-1 bg-background border border-border/40 text-sm text-white px-2 py-2 focus:outline-none focus:border-white/30" />
                        </div>
                        {error && <p className="text-xs text-red-300">{error}</p>}
                        <button type="submit" disabled={busy}
                            data-testid="reset-submit"
                            className="w-full py-2 bg-white text-black text-sm font-semibold disabled:opacity-40 hover:bg-gray-200 inline-flex items-center justify-center gap-2">
                            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                            Update password
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
